import React, {useState} from 'react';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import {PremiumWrapperOverlay} from '../../PremiumWrapperOverlay';
import {Col, Form, Row} from 'react-bootstrap';
import {Action} from '../../../system/buttons';
import {AnimeBarWidgetProps, srirachaFont} from '@pulsoid-private/widgets-lib';
import {NewFontManager} from '../../../system/NewFontManager/NewFontManager';

const DEFAULT_CONFIG: AnimeBarWidgetProps['configuration'] = {
  min: 50,
  max: 120,
  bpmText: {
    font: srirachaFont,
  },
  noImage: false,
  ranges: [
    {
      from: 0,
      to: 75,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-1.png',
    },
    {
      from: 76,
      to: 85,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-2.png',
    },
    {
      from: 86,
      to: 95,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-3.png',
    },
    {
      from: 96,
      to: 100,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-4.png',
    },
    {
      from: 101,
      to: 110,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-5.png',
    },
    {
      from: 111,
      to: 120,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-6.png',
    },
    {
      from: 121,
      to: 999,
      image:
        'https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/widget-source/anime-bar-by-YoshinoArt/anime-7.png',
    },
  ],
};

// TODO: Move to common utils
const getOverlappingSections = (ranges) => {
  const overlappingSections: [number, number][] = [];
  for (let i = 0; i < ranges.length; i++) {
    let {to} = ranges[i];
    if (i !== ranges.length - 1) {
      if (ranges[i + 1].to <= to) {
        overlappingSections.push([i, i + 1]);
      }
    }
  }
  return overlappingSections;
};

export const AnimeBarWidgetEdit = (props) => {
  const [configuration, setConfiguration] = React.useState(
    merge({}, DEFAULT_CONFIG, props.configuration)
  );
  const [overlappingSections, setOverlappingSections] = useState<
    [number, number][]
  >([]);

  const updateConfiguration = (configPart: Partial<typeof DEFAULT_CONFIG>) => {
    const newConfiguration = {...configuration, ...configPart};
    setConfiguration(newConfiguration);
    props.onUpdate(newConfiguration);
  };

  const handleOnSave = () => {
    props.onSave(configuration);
  };

  return (
    <>
      <PremiumWrapperOverlay
        bi={{
          from: 'widget_edit_premium_update',
          id: props.id,
        }}
        disabled={!props.premiumUpdate}
      >
        <h4>Bar fill configuration</h4>

        <Form.Group>
          <Row>
            <Col md="2">
              <Form.Label>Min</Form.Label>
              <Form.Control
                type="number"
                value={configuration.min}
                onChange={(e) =>
                  updateConfiguration({min: parseInt(e.target.value)})
                }
              />
            </Col>
          </Row>
        </Form.Group>

        <Form.Group>
          <Row>
            <Col md="2">
              <Form.Label>Max</Form.Label>
              <Form.Control
                type="number"
                value={configuration.max}
                onChange={(e) =>
                  updateConfiguration({max: parseInt(e.target.value)})
                }
              />
            </Col>
          </Row>
        </Form.Group>

        <h4 style={{marginTop: 40}}>Bottom Heart configuration</h4>

        <div style={{marginLeft: -30}}>
          <NewFontManager
            as={Col}
            onChange={(font) =>
              updateConfiguration({
                bpmText: {...configuration.bpmText, font},
              })
            }
            font={configuration?.bpmText?.font}
            withUpload
          />
        </div>

        <Form.Group as={Col} sm="2" className="p-0 ">
          <Form.Label column htmlFor="configuration.noImage">
            Show anime girls
          </Form.Label>
          <Col>
            <Form.Switch
              id="configuration.additionalText.show"
              defaultChecked={!configuration.noImage}
              onChange={(evt) =>
                updateConfiguration({
                  noImage: !(evt?.target as HTMLInputElement)?.checked,
                })
              }
            />
          </Col>
        </Form.Group>

        {!configuration.noImage && (
          <Form.Group>
            {configuration.ranges.map((range, index) => {
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 40,
                  }}
                >
                  <div style={{width: 130}}>
                    <Form.Control
                      value={range.to}
                      type="number"
                      onChange={(e) => {
                        const ranges = cloneDeep(configuration.ranges);
                        ranges[index].to = parseInt(e.target.value);

                        if (ranges[index + 1]) {
                          ranges[index + 1].from = parseInt(e.target.value) + 1;
                        }

                        const newOverlappingSections =
                          getOverlappingSections(ranges);

                        setOverlappingSections(newOverlappingSections);
                        updateConfiguration({ranges});
                      }}
                      isInvalid={overlappingSections.some((sections) =>
                        sections.includes(index)
                      )}
                    />
                    {overlappingSections.map((sections, secI) =>
                      sections.includes(index) ? (
                        <Form.Control.Feedback
                          type="invalid"
                          key={`${index}-${secI}`}
                          style={{
                            position: 'absolute',
                            width: 220,
                          }}
                        >
                          Overlapping sections
                        </Form.Control.Feedback>
                      ) : null
                    )}
                  </div>
                  <img src={range.image} alt="unicorn" style={{height: 100}} />
                </div>
              );
            })}
          </Form.Group>
        )}
      </PremiumWrapperOverlay>

      <Action disabled={overlappingSections.length > 0} onClick={handleOnSave}>
        Save settings
      </Action>
    </>
  );
};
